import React, { useState, useEffect } from 'react';
import { useEventBus } from '@byted/hooks';
import { useHistory } from '@jupiter/plugin-runtime/router';
import { Form, useFormState, useFormApi } from '@douyinfe/semi-ui';
import _ from 'lodash';
import styles from './index.module.scss';
import formData from './form.json';
import { PopContainer } from './pop-content';
import { FormData } from './utils';
import { PostType, Scope } from '@/typings/fe';
import { useScope } from '@/hooks/useScope';
// import { handleAsyncCallback } from '@/utils/common';
const ScopeMap: Record<Scope, string> = {
  merchant: '服务商',
  'mini-app': '小程序',
  'mini-game': '小游戏',
  other: '抖音其他开放能力',
  synthesize: '',
};

type Props = {
  onPostSubmit: (selectedItems: string[], list: Array<FormData[]>) => void;
};

export default ({ onPostSubmit }: Props) => {
  // 两个数组，一个维护当前选中的选项；一个维护选中后要展开的子选项
  const [selectedItems, setSelectedItems] = useState([] as string[]);
  const [list, setList] = useState([] as Array<FormData[]>);
  const history = useHistory();
  const scope = useScope();

  const formState = useFormState();
  const formApi = useFormApi();

  // 根据选项，更新需要展开的结构
  useEffect(() => {
    const curArray = new Array(selectedItems.length + 1).fill(0);
    curArray[0] = formData;

    for (let i = 1; i < curArray.length; i++) {
      const temp = curArray[i - 1].filter(
        (item: FormData) => item.label === selectedItems[i - 1],
      )[0];
      curArray[i] = temp.children;
    }

    setList(curArray);
  }, [selectedItems]);

  // 选项更新
  const handleSelectedItem = (
    start: number,
    remove: number,
    replace: string,
  ) => {
    const oldItems = Array.from(selectedItems);
    if (replace.length) {
      oldItems.splice(start, remove, replace);
    } else {
      oldItems.splice(start, remove);
    }
    setSelectedItems(oldItems);
  };

  useEventBus('POST_SUBMIT', (type: PostType) => {
    if (type !== 'question') {
      return;
    }
    onPostSubmit(selectedItems, list);
  });

  // 渲染基础信息
  const renderBasic = (data: FormData[]) =>
    data.map(item => (
      <PopContainer key={item.id ?? ''} description={item.description ?? ''}>
        <Form.Radio value={item.label}>{item.label}</Form.Radio>
      </PopContainer>
    ));

  // 渲染信息详情
  const renderDetail = () =>
    list[list.length - 1].map(item => {
      if (item.type === 'input') {
        return (
          <React.Fragment key={item.id}>
            <Form.Input
              field={item.label}
              label={item.label}
              rules={[
                { required: item.required, message: '' },
                {
                  validator: (_a, val, callback) => {
                    if (!val && item.required) {
                      callback(`请填写${item.label}`);
                      return false;
                    }
                    if (val?.length > 200) {
                      callback('最多输入 200 字');
                      return false;
                    }
                    callback();
                    return true;
                  },
                },
              ]}
              placeholder={
                item.placeholder ? item.placeholder : `请填写${item.label}`
              }
            />
            <div className={styles['input-description']}>
              {item.description}
            </div>
          </React.Fragment>
        );
      } else if (item.type === 'radio') {
        return (
          <Form.RadioGroup
            field={item.label}
            label={item.label}
            key={item.id}
            rules={[
              { required: item.required, message: `请选择${item.label}` },
            ]}>
            {item.children?.map(child => (
              <Form.Radio value={child.value} key={item.id + child.value}>
                {child.label}
              </Form.Radio>
            ))}
          </Form.RadioGroup>
        );
      } else if (item.type === 'textarea') {
        return (
          <Form.TextArea
            field={item.label}
            label={item.label}
            placeholder={item.description}
            rules={[
              {
                required: item.required,
                message: '',
              },
              {
                validator: (_a, val, callback) => {
                  if (!val) {
                    callback(`${item.description}, 最多输入 300 字`);
                    return false;
                  }
                  if (val.length > 300) {
                    callback('最多输入 300 字');
                    return false;
                  }
                  callback();
                  return true;
                },
              },
            ]}
            key={item.id}
            maxCount={300}
          />
        );
      } else if (item.type === 'select') {
        return (
          <Form.Select
            field={item.label}
            label={item.label}
            key={item.id}
            rules={[
              {
                required: item.required,
                message: `请选择${item.label}`,
              },
            ]}
            placeholder={`请选择${item.label}`}>
            {item.children?.map(child => (
              <Form.Select.Option
                value={child.value}
                key={item.id + child.value}
                rules={[
                  { required: item.required, message: `请选择${item.label}` },
                ]}>
                {child.label}
              </Form.Select.Option>
            ))}
          </Form.Select>
        );
      } else if (item.type === 'specialRadio') {
        return (
          <React.Fragment key={item.id}>
            <Form.RadioGroup
              field={item.label}
              label={item.label}
              rules={[{ required: item.required }]}>
              {item.children?.map(child => (
                <Form.Radio value={child.value} key={item.id + child.value}>
                  {child.label}
                </Form.Radio>
              ))}
            </Form.RadioGroup>
            {formApi.getValue('clientKey') === 'custom' ? (
              <Form.Input
                field="ClientKey"
                noLabel={true}
                placeholder="请填写Client Key名称"
              />
            ) : null}
          </React.Fragment>
        );
      } else {
        return null;
      }
    });

  useEffect(() => {
    formApi.setValue('levelTwo', null);
    handleSelectedItem(0, selectedItems.length, ScopeMap[scope]);
  }, [scope]);

  return (
    <React.Fragment>
      <Form.RadioGroup
        field="levelOne"
        label="发帖板块"
        type="pureCard"
        defaultValue={ScopeMap[scope]}
        initValue={ScopeMap[scope]}
        onChange={e => {
          const _scope = _.findKey(ScopeMap, value => value === e.target.value);
          history.replace({
            pathname: `/${_scope!}/post/new`,
          });
        }}
        rules={[{ required: true, message: '业务版块不能为空' }]}>
        {renderBasic(list[0] || [])}
      </Form.RadioGroup>
      {formState.values.levelOne ? (
        <Form.RadioGroup
          field="levelTwo"
          label="问题类型"
          onChange={e => {
            handleSelectedItem(1, selectedItems.length - 1, e.target.value);
            formApi.setValue('levelThree', null);
          }}
          rules={[{ required: true, message: '问题类型不能为空' }]}>
          {renderBasic(list[1] || [])}
        </Form.RadioGroup>
      ) : null}
      {formState.values.levelTwo ? (
        <Form.RadioGroup
          field="levelThree"
          label="问题模块"
          className={styles['three-level']}
          onChange={e => {
            handleSelectedItem(2, selectedItems.length - 2, e.target.value);
            formApi.setValue('levelFour', null);
          }}
          rules={[{ required: true, message: '问题模块不能为空' }]}>
          {renderBasic(list[2] || [])}
        </Form.RadioGroup>
      ) : null}
      {formState.values.levelOne === 'service' &&
      formState.values.levelThree === 'reviewProblem' ? (
        <React.Fragment>
          <Form.RadioGroup
            field="levelFour"
            label="审核类型"
            onChange={e => {
              handleSelectedItem(3, selectedItems.length - 1, e.target.value);
            }}
            rules={[{ required: true, message: '审核类型模块不能为空' }]}>
            {renderBasic(list[3] || [])}
          </Form.RadioGroup>
          {formState.values.levelFour ? (
            <React.Fragment>
              <div className={styles['basic-info']}> 详细信息 </div>
              {renderDetail()}
            </React.Fragment>
          ) : null}
        </React.Fragment>
      ) : null}
      {formState.values.levelThree &&
      !(
        formState.values.levelOne === 'service' &&
        formState.values.levelThree === 'reviewProblem'
      ) ? (
        <React.Fragment>
          <div className={styles['basic-info']}> 详细信息 </div>
          {renderDetail()}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};
